import { type Provenance, ProvenanceSource } from '@ardoq/api-types';
import { IconName } from '@ardoq/icons';

type ProvenanceSourceOf<ActorKind extends Provenance['actorKind']> = Extract<
  Provenance,
  { actorKind: ActorKind }
>['source'];

export type SourceDisplay = {
  iconName: IconName;
  name: string;
};

const TRANSFORMATION_FUTURE_STATE_SOURCE: SourceDisplay = {
  iconName: IconName.TRANSFORMATION_FUTURE_STATE,
  name: 'Transformation Future State',
};

const TRANSFORMATION_SOURCE: SourceDisplay = {
  iconName: IconName.PATH,
  name: 'Transformation',
};

export const uiSources: Record<
  Exclude<ProvenanceSourceOf<'ui'>, 'unknown'>,
  SourceDisplay
> = {
  [ProvenanceSource.DATA_INVENTORY]: {
    iconName: IconName.INVENTORY,
    name: 'Data Inventory',
  },
  [ProvenanceSource.COMPONENT_OVERVIEW]: {
    iconName: IconName.COMPONENT,
    name: 'Component Overview',
  },
  [ProvenanceSource.DATA_EXPLORER]: {
    iconName: IconName.NAVIGATE,
    name: 'Data Explorer',
  },
  [ProvenanceSource.GRID_EDITOR]: {
    iconName: IconName.GRID_ON,
    name: 'Grid Editor',
  },
  [ProvenanceSource.SHIFTX]: {
    iconName: IconName.SHIFTX,
    name: 'ShiftX',
  },
  'scenario-merge': {
    iconName: IconName.SCENARIO_MERGE,
    name: 'Scenario Merge',
  },
  'component-merge': {
    iconName: IconName.SCENARIO_MERGE,
    name: 'Component Merge',
  },
  'reference-merge': {
    iconName: IconName.SCENARIO_MERGE,
    name: 'Reference Merge',
  },
  'component-copy': {
    iconName: IconName.COPY,
    name: 'Component Copy',
  },
  'workspace-copy': {
    iconName: IconName.COPY,
    name: 'Workspace Copy',
  },
  'bundle-copy': {
    iconName: IconName.FILE_COPY,
    name: 'Bundle Copy',
  },
  'solution-install': {
    iconName: IconName.SOLUTIONS,
    name: 'Solution Install',
  },
  'solution-upgrade': {
    iconName: IconName.SOLUTIONS,
    name: 'Solution Upgrade',
  },
  'use-case-start': {
    iconName: IconName.SOLUTIONS,
    name: 'Use Case Start',
  },
  'asset-delete': {
    iconName: IconName.DELETE,
    name: 'Asset Delete',
  },
  'workspace-delete': {
    iconName: IconName.DELETE,
    name: 'Workspace Delete',
  },
  'metamodel-change': {
    iconName: IconName.METAMODEL,
    name: 'Metamodel Change',
  },
  'future-state-merge': TRANSFORMATION_FUTURE_STATE_SOURCE,
  'transform-future-state-approve': TRANSFORMATION_FUTURE_STATE_SOURCE,
  'transform-future-state-copy': TRANSFORMATION_FUTURE_STATE_SOURCE,
  'transform-future-state-create': TRANSFORMATION_FUTURE_STATE_SOURCE,
  'transform-future-state-delete': TRANSFORMATION_FUTURE_STATE_SOURCE,
  'transform-future-state-from-scenario': TRANSFORMATION_FUTURE_STATE_SOURCE,
  'transform-future-state-unapprove': TRANSFORMATION_FUTURE_STATE_SOURCE,
  'transform-future-state-update': TRANSFORMATION_FUTURE_STATE_SOURCE,
  'transform-initiative-create': TRANSFORMATION_SOURCE,
  'transform-initiative-delete': TRANSFORMATION_SOURCE,
  'transform-initiative-update': TRANSFORMATION_SOURCE,
};

export const apiSources: Record<ProvenanceSourceOf<'api'>, SourceDisplay> = {
  'api-v1': {
    iconName: IconName.API,
    name: 'API v1',
  },
  'public-api': {
    iconName: IconName.API,
    name: 'Public REST API',
  },
};

export const surveySources: Record<
  ProvenanceSourceOf<'survey'>,
  SourceDisplay
> = {
  'survey-response': {
    iconName: IconName.SURVEYS,
    name: 'Survey Response',
  },
  'survey-approval': {
    iconName: IconName.SURVEYS,
    name: 'Survey Approval',
  },
  'change-request-approval': {
    iconName: IconName.HISTORY,
    name: 'Change Request Approval',
  },
};

export const aiAgentSources: Record<
  ProvenanceSourceOf<'ai-agent'>,
  SourceDisplay
> = {
  'ardoq-assistant': {
    iconName: IconName.AI_STAR,
    name: 'Ardoq Assistant',
  },
  mcp: {
    iconName: IconName.AI_STAR,
    name: 'Ardoq MCP',
  },
};

export const unlistedAiAgentSource: SourceDisplay = {
  iconName: IconName.AGENT,
  name: 'AI Agent',
};

const isListedSource = <Source extends string>(
  sources: Record<Source, SourceDisplay>,
  source: string
): source is Source => Object.hasOwn(sources, source);

export const getSourceDisplay = <Source extends string>(
  sources: Record<Source, SourceDisplay>,
  source: string
): SourceDisplay | undefined =>
  isListedSource(sources, source) ? sources[source] : undefined;
